import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave } from '@fortawesome/free-solid-svg-icons';
import { Beneficiary, BeneficiaryFormData } from '../../interfaces/beneficiary';
import { getBeneficiary, createBeneficiary, updateBeneficiary } from '../../services/beneficiaryService';
import './BeneficiaryForm.css';

// Expresión regular para validar DNI
const dniRegex = /^\d{6,10}$/;

const BeneficiaryForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<BeneficiaryFormData>({
    dni: null,
    has_physical_dni: false,
    author: null,
    author_details: null,
    first_name: null,
    last_name: null,
    nickname: null,
    birthdate: null,
    phone_number: null,
    reference_phone_number: null,
    geolocation: null,
    address: null,
    address_reference: null,
    sex: null,
    nationality: null,
    is_fixed_or_transitory_place: false,
    life_center: null,
    has_family_contact: '',
    subsidies: '',
    health_info: null
  });
  
  // Cargar datos del beneficiario si estamos en modo edición
  useEffect(() => {
    if (isEditMode && id) {
      const fetchBeneficiary = async () => {
        try {
          setLoading(true);
          const data = await getBeneficiary(parseInt(id));
          // Extraer solo los campos que necesitamos para el formulario
          const {
            id: _id, // No lo incluimos en formData
            created_at: _created, // No lo incluimos en formData
            updated_at: _updated, // No lo incluimos en formData
            ...formFields
          } = data;
          
          setFormData(formFields);
          setError(null);
        } catch (err) {
          console.error('Error fetching beneficiary:', err);
          setError('No se pudo cargar la información del beneficiario. Por favor, intente nuevamente.');
        } finally {
          setLoading(false);
        }
      };
      
      fetchBeneficiary();
    }
  }, [id, isEditMode]);
  
  // Manejar cambios en los inputs
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    // Manejar inputs de tipo checkbox
    if (type === 'checkbox') {
      const isChecked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: isChecked
      }));
      return;
    }
    
    // Manejar el resto de los inputs
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? null : value // Convertir valores vacíos a null
    }));
  };
  
  // Validar el formulario
  const validateForm = (): boolean => {
    // Validar DNI si está presente
    if (formData.dni && !dniRegex.test(formData.dni)) {
      setError('El DNI debe contener entre 6 y 10 dígitos numéricos.');
      return false;
    }
    
    return true;
  };
  
  // Manejar el envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Si estamos en modo edición, actualizar; de lo contrario, crear
      if (isEditMode && id) {
        await updateBeneficiary(parseInt(id), formData);
      } else {
        await createBeneficiary(formData);
      }
      
      // Redireccionar a la lista de beneficiarios
      navigate('/beneficiaries');
    } catch (err: any) {
      console.error('Error saving beneficiary:', err);
      
      if (err.response && err.response.data) {
        // Mostrar errores específicos del backend si están disponibles
        const backendErrors = Object.entries(err.response.data)
          .map(([field, messages]) => `${field}: ${messages}`)
          .join(', ');
        
        setError(`Error al guardar: ${backendErrors}`);
      } else {
        setError('No se pudo guardar la información. Por favor, intente nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  if (loading && isEditMode) {
    return <div className="loading-container">Cargando información del beneficiario...</div>;
  }
  
  return (
    <div className="beneficiary-form-container">
      <div className="flex justify-between align-center mb-4">
        <div className="flex align-center">
          <button 
            className="btn btn-secondary mr-3"
            onClick={() => navigate('/beneficiaries')}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Volver
          </button>
          <h1>{isEditMode ? 'Editar Beneficiario' : 'Nuevo Beneficiario'}</h1>
        </div>
      </div>
      
      {error && <div className="alert alert-danger mb-4">{error}</div>}
      
      <form onSubmit={handleSubmit} className="card">
        <div className="form-section">
          <h2>Información Personal</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name" className="form-label">Nombre</label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                className="form-control"
                value={formData.first_name || ''}
                onChange={handleChange}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="last_name" className="form-label">Apellido</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                className="form-control"
                value={formData.last_name || ''}
                onChange={handleChange}
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="nickname" className="form-label">Apodo</label>
              <input
                type="text"
                id="nickname"
                name="nickname"
                className="form-control"
                value={formData.nickname || ''}
                onChange={handleChange}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="birthdate" className="form-label">Fecha de Nacimiento</label>
              <input
                type="date"
                id="birthdate"
                name="birthdate"
                className="form-control"
                value={formData.birthdate || ''}
                onChange={handleChange}
              />
              <small className="form-text text-muted">Formato: YYYY-MM-DD</small>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dni" className="form-label">DNI</label>
              <input
                type="text"
                id="dni"
                name="dni"
                className="form-control"
                value={formData.dni || ''}
                onChange={handleChange}
              />
              <small className="form-text text-muted">Entre 6 y 10 dígitos numéricos</small>
            </div>
            
            <div className="form-group">
              <div className="checkbox-item mt-4">
                <input
                  type="checkbox"
                  id="has_physical_dni"
                  name="has_physical_dni"
                  checked={formData.has_physical_dni}
                  onChange={handleChange}
                />
                <label htmlFor="has_physical_dni">Tiene DNI físico</label>
              </div>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="sex" className="form-label">Sexo</label>
              <select
                id="sex"
                name="sex"
                className="form-control"
                value={formData.sex || ''}
                onChange={handleChange}
              >
                <option value="">Seleccione...</option>
                <option value="Masculino">Masculino</option>
                <option value="Femenino">Femenino</option>
                <option value="Otro">Otro</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="nationality" className="form-label">Nacionalidad</label>
              <input
                type="text"
                id="nationality"
                name="nationality"
                className="form-control"
                value={formData.nationality || ''}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
        
        <div className="form-section">
          <h2>Información de Contacto</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phone_number" className="form-label">Teléfono</label>
              <input
                type="text"
                id="phone_number"
                name="phone_number"
                className="form-control"
                value={formData.phone_number || ''}
                onChange={handleChange}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="reference_phone_number" className="form-label">Referencia del Teléfono</label>
              <textarea
                id="reference_phone_number"
                name="reference_phone_number"
                className="form-control"
                value={formData.reference_phone_number || ''}
                onChange={handleChange}
                rows={2}
              ></textarea>
              <small className="form-text text-muted">¿De quién es el número?</small>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="address" className="form-label">Dirección</label>
              <input
                type="text"
                id="address"
                name="address"
                className="form-control"
                value={formData.address || ''}
                onChange={handleChange}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="address_reference" className="form-label">Referencia de Dirección</label>
              <textarea
                id="address_reference"
                name="address_reference"
                className="form-control"
                value={formData.address_reference || ''}
                onChange={handleChange}
                rows={2}
              ></textarea>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="geolocation" className="form-label">Geolocalización</label>
              <input
                type="text"
                id="geolocation"
                name="geolocation"
                className="form-control"
                value={formData.geolocation || ''}
                onChange={handleChange}
                placeholder="Latitud, Longitud"
              />
              <small className="form-text text-muted">Ingresar coordenadas X, Y</small>
            </div>
            
            <div className="form-group">
              <div className="checkbox-item mt-4">
                <input
                  type="checkbox"
                  id="is_fixed_or_transitory_place"
                  name="is_fixed_or_transitory_place"
                  checked={formData.is_fixed_or_transitory_place}
                  onChange={handleChange}
                />
                <label htmlFor="is_fixed_or_transitory_place">Ubicación transitoria</label>
              </div>
            </div>
          </div>
        </div>
        
        <div className="form-section">
          <h2>Información Adicional</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="life_center" className="form-label">Centro de Vida</label>
              <input
                type="text"
                id="life_center"
                name="life_center"
                className="form-control"
                value={formData.life_center || ''}
                onChange={handleChange}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="author_details" className="form-label">Autor</label>
              <input
                type="text"
                id="author_details"
                name="author_details"
                className="form-control"
                value={formData.author_details || ''}
                onChange={handleChange}
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="has_family_contact" className="form-label">Contacto Familiar</label>
              <textarea
                id="has_family_contact"
                name="has_family_contact"
                className="form-control"
                value={formData.has_family_contact}
                onChange={handleChange}
                rows={3}
              ></textarea>
              <small className="form-text text-muted">¿Tiene alguna relación o contacto con familiares? Especificar.</small>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="subsidies" className="form-label">Subsidios</label>
              <textarea
                id="subsidies"
                name="subsidies"
                className="form-control"
                value={formData.subsidies}
                onChange={handleChange}
                rows={3}
              ></textarea>
              <small className="form-text text-muted">¿Recibe algún subsidio o ayuda financiera?</small>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="health_info" className="form-label">Información de Salud</label>
              <textarea
                id="health_info"
                name="health_info"
                className="form-control"
                value={formData.health_info || ''}
                onChange={handleChange}
                rows={4}
              ></textarea>
              <small className="form-text text-muted">Describa condiciones de salud relevantes, tratamientos en curso o necesidades recurrentes.</small>
            </div>
          </div>
        </div>
        
        <div className="form-actions">
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={() => navigate('/beneficiaries')}
          >
            Cancelar
          </button>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Guardando...' : (
              <>
                <FontAwesomeIcon icon={faSave} className="mr-1" /> 
                {isEditMode ? 'Actualizar' : 'Guardar'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BeneficiaryForm; 